import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SriLankaMap from '../components/Travel/SriLankaMap';
import Footer from '../components/Footer';

const Travel = () => {
    const [viewedDistrict, setViewedDistrict] = useState(null);
    const navigate = useNavigate();

    return (
        <div className="bg-white pt-32 min-h-screen">
            <div className="container mx-auto px-6 max-w-7xl pb-24">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em] mb-4 block"
                    >
                        Interactive Archive
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-6xl font-bold text-slate-900 font-display mb-4"
                    >
                        Regional <span className="text-emerald-600 italic">Exploration.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-lg text-slate-500 font-medium leading-relaxed max-w-3xl mx-auto italic"
                    >
                        Select a district on the map to explore its unique biodiversity, iconic destinations, and protected jungle habitats.
                    </motion.p>
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    {/* Left Column: Map */}
                    <div className="lg:col-span-7 relative bg-slate-50/50 rounded-[40px] border border-slate-100 p-8 overflow-hidden min-h-[600px] flex items-center justify-center">
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-50 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 opacity-60" />
                        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-slate-100 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/2 opacity-60" />

                        <div className="relative z-10 w-full">
                            <SriLankaMap onDistrictHover={setViewedDistrict} />
                        </div>
                    </div>

                    {/* Right Column: Details Panel */}
                    <div className="lg:col-span-5 relative h-full">
                        <AnimatePresence mode='wait'>
                            {viewedDistrict ? (
                                <motion.div
                                    key={viewedDistrict.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="bg-white border border-slate-100 rounded-[40px] p-10 shadow-xl sticky top-32"
                                >
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4 font-sans">District Intel</span>
                                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 font-display">{viewedDistrict.name}</h2>
                                    <div className="h-1.5 w-24 bg-emerald-500 rounded-full mb-6" />

                                    <p className="text-slate-500 leading-relaxed mb-8 text-sm font-sans">
                                        {viewedDistrict.description}
                                    </p>

                                    <div className="grid gap-6">
                                        <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest block mb-3">Key Destinations</span>
                                            <div className="flex flex-wrap gap-2">
                                                {viewedDistrict.topPlaces?.map((place) => (
                                                    <span key={place} className="px-3 py-1.5 bg-white border border-emerald-100 text-xs font-bold text-emerald-800 rounded-full shadow-sm">
                                                        {place}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="p-6 bg-slate-900 rounded-3xl text-white relative overflow-hidden">
                                            <div className="relative z-10">
                                                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-3">Biodiversity Hotspots</span>
                                                <div className="flex flex-wrap gap-2">
                                                    {viewedDistrict.topJungles?.map((jungle) => (
                                                        <span key={jungle} className="px-3 py-1.5 bg-white/10 text-xs font-bold text-emerald-50 rounded-full backdrop-blur-md">
                                                            {jungle}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-2xl" />
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => navigate(`/travel/district/${viewedDistrict.id}`)}
                                        className="w-full mt-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-colors flex items-center justify-center gap-2 group"
                                    >
                                        Explore Full Profile
                                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="h-[600px] flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-slate-200 rounded-[40px]"
                                >
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                                        <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-300 mb-2">Explore the Island</h3>
                                    <p className="text-slate-400 max-w-xs mx-auto text-sm">Select a district on the map to reveal detailed ecological data and travel guides.</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Footer Insight Grid (Existing) */}
                <div className="mt-24 grid md:grid-cols-3 gap-8">
                    <div className="p-8 bg-white border border-slate-100 rounded-[32px] shadow-sm">
                        <span className="text-emerald-600 font-black text-[10px] uppercase tracking-widest mb-4 block font-sans">01. Selection</span>
                        <h4 className="text-lg font-bold text-slate-900 mb-3 font-display">Choose Your Region</h4>
                        <p className="text-slate-500 text-sm leading-relaxed font-sans">Each of Sri Lanka's 25 districts holds a distinct ecological profile.</p>
                    </div>
                    <div className="p-8 bg-white border border-slate-100 rounded-[32px] shadow-sm">
                        <span className="text-emerald-600 font-black text-[10px] uppercase tracking-widest mb-4 block font-sans">02. Discovery</span>
                        <h4 className="text-lg font-bold text-slate-900 mb-3 font-display">Analyze the Data</h4>
                        <p className="text-slate-500 text-sm leading-relaxed font-sans">Hover to reveal top destinations and critical jungle corridors.</p>
                    </div>
                    <div className="p-8 bg-white border border-slate-100 rounded-[32px] shadow-sm">
                        <span className="text-emerald-600 font-black text-[10px] uppercase tracking-widest mb-4 block font-sans">03. Action</span>
                        <h4 className="text-lg font-bold text-slate-900 mb-3 font-display">Deep Dive Details</h4>
                        <p className="text-slate-500 text-sm leading-relaxed font-sans">Click through for high-fidelity maps and weather patterns.</p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Travel;
